import React from 'react';
import { Input } from '../';

/**
 * Password field for a secret the server stores but never sends back.
 *
 * Such a field is always empty when a form opens, whether or not a secret is stored, so an empty
 * field cannot mean "remove it" -- it is what an untouched form looks like. Removal is therefore a
 * separate intent the field reports through onRemoveChange, and the caller sends to the API
 * alongside the value.
 *
 * The placeholder is what tells the user which state they are looking at: a secret is stored, or the
 * stored one is about to be removed. Nothing stored shows no placeholder at all.
 *
 * Typing withdraws a pending removal, which means one keystroke reports two things: the new value
 * and the withdrawal. A call site that holds both in the same state object has to apply them from
 * the previous state (setState(prev => ...)) rather than from the state its render closed over, or
 * the second update writes the withdrawal back to what it was and the removal cannot be undone.
 *
 * @param {string}   name                 - Field name, passed back on every onChange.
 * @param {string}   [label]              - Label text.
 * @param {string}   value                - What the user has typed, '' when they have typed nothing.
 * @param {boolean}  [isSet=false]        - Whether the server holds a secret for this field. The clear
 *                                         button appears only when one is held and the field is empty.
 * @param {boolean}  [isPendingRemoval=false] - Whether the stored secret is marked for removal.
 * @param {Function} onChange             - Called with { name, value } as the user types.
 * @param {Function} onRemoveChange       - Called with true when the user asks for the stored secret
 *                                         to be removed, and with false when typing cancels that.
 * @param {boolean}  [isNew=false]        - Adding rather than editing, so there is nothing stored yet
 *                                         and the field behaves like an ordinary password input.
 * @param {string}   [placeholder]        - Placeholder while adding.
 * @param {boolean}  [disabled=false]     - Disables the field.
 * @param {string}   [title]              - Tooltip text on the label.
 * @param {{ valid: boolean|string, message: string }} [validation] - Validation state.
 * @returns {JSX.Element}
 */
export const SecretInput = ({
  name,
  label,
  value,
  isSet = false,
  isPendingRemoval = false,
  onChange,
  onRemoveChange,
  isNew = false,
  placeholder,
  disabled = false,
  title,
  validation,
}) => {
  /**
   * Typing cancels a removal: the user is replacing the secret rather than taking it away.
   * @param {{ name: string, value: string }} element
   */
  const handleChange = element => {
    if (isPendingRemoval && element.value !== '') {
      onRemoveChange(false);
    }
    onChange(element);
  };

  // Only offer removal when there is something to remove, it is not already marked for it, and the
  // field is empty. The button is handed to Input as onClear because the field's own value cannot
  // express the intent.
  //
  // The empty-field condition is what keeps the button honest. A secret typed into the field is a
  // replacement, so a clear button next to it would read as "discard what I typed" while doing
  // something else entirely -- and the pair it would produce, a new secret alongside a request to
  // remove the stored one, states two opposite intents that the API refuses rather than guesses at.
  // Clearing the typed value restores the button.
  const canRemove = !isNew && isSet && !isPendingRemoval && value === '';

  // Nothing stored and nothing pending leaves the field blank: there is no state to describe, and a
  // placeholder saying so would be read as a value.
  const currentPlaceholder = isNew
    ? placeholder
    : isPendingRemoval
      ? 'Will be removed on save'
      : isSet
        ? '< hidden >'
        : placeholder;

  return (
    <Input
      type="password"
      name={name}
      label={label}
      value={value}
      onChange={handleChange}
      isNullable={canRemove}
      onClear={canRemove ? () => onRemoveChange(true) : undefined}
      clearTooltip="Clear saved password"
      autoComplete="new-password"
      disabled={disabled}
      title={title}
      placeholder={currentPlaceholder}
      validation={validation}
    />
  );
};
